<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CampaignDrawer from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignDrawer.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import LiveChatCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/LiveChatCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const drawerRef = ref(null);
const isDrawerOpen = ref(false);
const confirmDeleteCampaignDialogRef = ref(null);
const selectedCampaign = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const liveChatCampaigns = computed(
  () => getters['campaigns/getLiveChatCampaigns'].value
);

const hasNoLiveChatCampaigns = computed(
  () => liveChatCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const editingCampaign = ref(null);

const handleEdit = campaign => {
  drawerRef.value.runGuarded(() => {
    editingCampaign.value = campaign;
    isDrawerOpen.value = true;
  });
};

// A gaveta pede confirmação antes de trocar de campanha com alterações sem salvar.
const handleCreate = () => {
  drawerRef.value.runGuarded(() => {
    editingCampaign.value = null;
    isDrawerOpen.value = true;
  });
};

// A gaveta é coluna ao lado da lista (não modal): se a campanha aberta nela for excluída pelo menu ⋮, a gaveta fecha
// (não há mais o que salvar).
watch(liveChatCampaigns, campaigns => {
  const id = editingCampaign.value?.id;
  if (!isDrawerOpen.value || !id) return;
  if (campaigns?.some(campaign => campaign.id === id)) return;
  isDrawerOpen.value = false;
  editingCampaign.value = null;
});

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.LIVE_CHAT.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.LIVE_CHAT.NEW_CAMPAIGN')"
    @click="handleCreate"
  >
    <CampaignList
      v-if="isFetchingCampaigns || !hasNoLiveChatCampaigns"
      :campaigns="liveChatCampaigns"
      is-live-chat-type
      :title="t('CAMPAIGN.LIVE_CHAT.HEADER_TITLE')"
      :is-loading="isFetchingCampaigns"
      @edit="handleEdit"
      @delete="handleDelete"
    />
    <LiveChatCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.LIVE_CHAT.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.LIVE_CHAT.EMPTY_STATE.SUBTITLE')"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <template #drawer>
      <CampaignDrawer
        ref="drawerRef"
        v-model:open="isDrawerOpen"
        type="live-chat"
        :campaign="editingCampaign"
      />
    </template>
  </CampaignLayout>
</template>
